import React, {useCallback, useEffect, useRef, useState} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import GlobalStyles from '@mui/material/GlobalStyles';
import {deferTask} from '@jobseeker/common-core/utils/common';
import {
  TrackEventUBA,
  getUbaPageName,
} from '@jobseeker/common-core/utils/analytics';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import {
  TalentBridgeCalendarIcon,
  TalentBridgeHeroBg,
  TalentBridgeIllustration,
  TalentBridgeLogo,
  TalentBridgeMapIcon,
} from '@jobseeker/lotus/assets/icons';
import {
  fetchTalentBridgeAllLocations,
  talentBridgeContants,
  extractDate,
} from './utils';

const ORANGE = '#E85E00';
const INK = '#1f2430';
const SHADOW =
  '0 18px 40px -12px rgba(20,25,40,.35),0 4px 12px rgba(20,25,40,.12)';

// Bundled image imports resolve to a plain string in some build setups and to a
// StaticImageData-like object ({src, width, height}) in others - normalize to a
// string so it can be safely used inside an <img src> / CSS url(...).
const resolveAssetUrl = (asset: unknown): string =>
  typeof asset === 'string' ? asset : (asset as {src: string})?.src ?? '';

const heroBgUrl = resolveAssetUrl(TalentBridgeHeroBg);
const logoUrl = resolveAssetUrl(TalentBridgeLogo);
const illustrationUrl = resolveAssetUrl(TalentBridgeIllustration);
const calendarIconUrl = resolveAssetUrl(TalentBridgeCalendarIcon);
const mapIconUrl = resolveAssetUrl(TalentBridgeMapIcon);

const CONFETTI_COLORS = [
  '#F0491E',
  '#FF8A3D',
  '#FFB347',
  '#FFD37A',
  '#22a565',
  '#ffffff',
];

// Fire the confetti burst at most once per browser session, even if the
// 'popup' variant remounts (e.g. client-side route changes back to /jobfeed).
let hasFiredPopupConfetti = false;

const POPUP_FREQUENCY_DAYS = 2;
const POPUP_LAST_SHOWN_KEY = 'talent_bridge_popup_last_shown';

const startOfDay = (ts: number): number => {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

// Reads/writes localStorage, so callers must only invoke this client-side.
const canShowPopupToday = (): boolean => {
  try {
    const lastShown = parseInt(
      localStorage.getItem(POPUP_LAST_SHOWN_KEY) ?? '0',
      10
    );
    if (lastShown) {
      const calendarDaysSince = Math.round(
        (startOfDay(Date.now()) - startOfDay(lastShown)) / 86_400_000
      );
      if (calendarDaysSince < POPUP_FREQUENCY_DAYS) return false;
    }
  } catch {
    return false;
  }
  return true;
};

// True when today falls within the drive window "DD/MM/YYYY - DD/MM/YYYY".
const isDriveLive = (eventDates?: string): boolean => {
  if (!eventDates) return false;
  const [startStr, endStr] = eventDates.split(' - ');
  if (!startStr || !endStr) return false;
  const [d1, m1, y1] = startStr.split('/').map(Number);
  const [d2, m2, y2] = endStr.split('/').map(Number);
  const startDate = new Date(y1, m1 - 1, d1);
  const endDate = new Date(y2, m2 - 1, d2);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today >= startDate && today <= endDate;
};

type TalentBridgePromoSliderProps = {
  // 'slider' = fixed docked card (desktop); 'banner' = inline full-width banner;
  // 'popup' = centered modal that auto-docks into a slim bottom bar
  variant?: 'slider' | 'banner' | 'popup';
};

const TalentBridgePromoSlider: React.FC<TalentBridgePromoSliderProps> = ({
  variant = 'slider',
}) => {
  const [city, setCity] = useState<string>('');
  const [eventDates, setEventDates] = useState<string | null>(null);
  const [driveText, setDriveText] = useState<string>('');
  const [driveDate, setDriveDate] = useState<string>('');
  const [driveVenue, setDriveVenue] = useState<string>('');
  const [displayDate, setDisplayDate] = useState<string>('');
  const [driveCta, setDriveCta] = useState<string>('');
  const [loading, setLoading] = useState(true);

  // view state (mirrors the reference HTML's `in` / `exit-down` classes)
  const [promoIn, setPromoIn] = useState(false);
  const [promoExit, setPromoExit] = useState(false);
  const [miniIn, setMiniIn] = useState(false);

  // 'popup' variant view state: 0 = modal open, 1 = docked into the bottom bar.
  // The bottom bar always shows (no dismiss control); only the modal card is
  // subject to the 2-day cooldown, so it decides the *starting* stage - when
  // the cooldown is still active, skip straight to the bar (no modal, no
  // confetti) rather than gating the whole variant off.
  const [stage, setStage] = useState<0 | 1>(() =>
    typeof window !== 'undefined' && canShowPopupToday() ? 0 : 1
  );
  // Captured once at mount, before any later setStage(1) transition (the
  // auto-dock timer, or a manual dockPopup) can change `stage`: true only when
  // the cooldown was already active and the mid modal never rendered at all.
  const startedAtBottomRef = useRef(stage === 1);
  const [popupIn, setPopupIn] = useState(false);
  const confettiCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const hasMarkedPopupShownRef = useRef(false);
  const hasFiredPopupMidViewRef = useRef(false);
  const hasFiredPopupBottomViewRef = useRef(false);
  const hasFiredSliderViewRef = useRef(false);
  const hasFiredBannerViewRef = useRef(false);
  const isDesktopViewport = useIsLargeDevice('sm');

  useEffect(() => {
    const fetchBanner = async () => {
      try {
        const obj = await fetchTalentBridgeAllLocations();
        // Pick the city whose drive is LIVE today (showBanner + date window),
        // so the "Apply" link opens that location's Talent Bridge page.
        let activeCityKey = '';
        for (const cityKey in obj) {
          const data = obj[cityKey];
          if (data?.showBanner === 'true' && isDriveLive(data?.eventDates)) {
            activeCityKey = cityKey;
            break;
          }
        }
        if (activeCityKey) {
          const data = obj[activeCityKey];
          setCity(activeCityKey);
          setDriveText(data?.text || '');
          setDriveDate(data?.date || '');
          setDriveVenue(data?.venue || '');
          setDisplayDate(extractDate(data?.date || '', data?.month || ''));
          setDriveCta(data?.ctaText || '');
          setEventDates(data?.eventDates || '');
        }
      } catch (err) {
        console.error('Error fetching drive banner data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchBanner();
  }, []);

  // Only show while the selected drive is live.
  const showBanner = isDriveLive(eventDates || undefined);

  // Entrance: slide the promo in shortly after it becomes eligible
  useEffect(() => {
    if (!showBanner) return undefined;
    const timer = setTimeout(() => setPromoIn(true), 700);
    return () => clearTimeout(timer);
  }, [showBanner]);

  const closePromo = useCallback(() => {
    deferTask(() => {
      setPromoIn(false);
      setPromoExit(true);
      // let the promo slide down before the pill pops up
      setTimeout(() => setMiniIn(true), 180);
    });
  }, []);

  const openPromo = useCallback(() => {
    deferTask(() => {
      setMiniIn(false);
      setPromoExit(false);
      // reset to the off-screen-left base state, then replay the slide-in
      setPromoIn(false);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setPromoIn(true));
      });
    });
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && promoIn) closePromo();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [promoIn, closePromo]);

  // 'popup' variant: once the modal is actually being shown (stage 0, on an
  // eligible desktop/live-drive render), mark it shown exactly once - starts
  // the next 2-day cooldown window from this appearance.
  useEffect(() => {
    if (variant !== 'popup' || !isDesktopViewport || !showBanner) return;
    if (stage !== 0 || hasMarkedPopupShownRef.current) return;
    hasMarkedPopupShownRef.current = true;
    try {
      localStorage.setItem(POPUP_LAST_SHOWN_KEY, String(Date.now()));
    } catch {
      // localStorage unavailable (e.g. private browsing) - non-critical,
      // it just won't persist the cooldown for next time.
    }
  }, [variant, isDesktopViewport, showBanner, stage]);

  useEffect(() => {
    if (variant !== 'popup' || !isDesktopViewport || !showBanner) return;
    const firedRef = startedAtBottomRef.current
      ? hasFiredPopupBottomViewRef
      : hasFiredPopupMidViewRef;
    if (firedRef.current) return;
    firedRef.current = true;
    TrackEventUBA('adBannerView', 'view', {
      source: `HTB ${getUbaPageName()} ${
        startedAtBottomRef.current ? 'popup-bottom' : 'popup-mid'
      }`,
    });
  }, [variant, isDesktopViewport, showBanner]);

  useEffect(() => {
    if (!showBanner) return;
    if (variant === 'slider' && !hasFiredSliderViewRef.current) {
      hasFiredSliderViewRef.current = true;
      TrackEventUBA('adBannerView', 'view', {
        source: `HTB ${getUbaPageName()} slider`,
      });
    }
    if (variant === 'banner' && !hasFiredBannerViewRef.current) {
      hasFiredBannerViewRef.current = true;
      TrackEventUBA('adBannerView', 'view', {
        source: `HTB ${getUbaPageName()} banner`,
      });
    }
  }, [variant, showBanner]);

  // 'popup' variant: bounce the modal in shortly after it becomes eligible,
  // then auto-dock it into the bottom bar if the user hasn't already dismissed it.
  useEffect(() => {
    if (variant !== 'popup' || !showBanner || stage !== 0) return undefined;
    const timer = setTimeout(() => setPopupIn(true), 50);
    return () => clearTimeout(timer);
  }, [variant, showBanner, stage]);

  useEffect(() => {
    if (variant !== 'popup' || !showBanner || stage !== 0) return undefined;
    const timer = setTimeout(() => setStage(1), 5000);
    return () => clearTimeout(timer);
  }, [variant, showBanner, stage]);

  useEffect(() => {
    // isDesktopViewport is false on the very first client render (useMediaQuery
    // can't know the real viewport until after mount), so the canvas isn't in
    // the DOM yet on that pass - must be a dep so this effect retries once it
    // flips true and the canvas ref actually attaches, instead of firing once
    // against a null ref and never running again.
    if (
      variant !== 'popup' ||
      !isDesktopViewport ||
      !showBanner ||
      stage !== 0 ||
      !popupIn
    ) {
      return undefined;
    }
    if (hasFiredPopupConfetti) return undefined;
    const canvas = confettiCanvasRef.current;
    if (!canvas) return undefined;
    if (
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    ) {
      return undefined;
    }

    let cancelled = false;
    const timers: ReturnType<typeof setTimeout>[] = [];

    import('canvas-confetti').then(({default: confetti}) => {
      if (cancelled) return;
      hasFiredPopupConfetti = true;
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      const fireFrom = confetti.create(canvas, {
        resize: false,
        useWorker: false,
      });
      const fire = (opts: Parameters<typeof fireFrom>[0]) =>
        fireFrom({
          startVelocity: 45,
          ticks: 220,
          colors: CONFETTI_COLORS,
          ...opts,
        });

      fire({particleCount: 160, spread: 100, origin: {y: 0.5}});
      timers.push(
        setTimeout(
          () =>
            fire({
              particleCount: 90,
              angle: 60,
              spread: 80,
              origin: {x: 0, y: 0.65},
            }),
          160
        )
      );
      timers.push(
        setTimeout(
          () =>
            fire({
              particleCount: 90,
              angle: 120,
              spread: 80,
              origin: {x: 1, y: 0.65},
            }),
          160
        )
      );
    });

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [variant, isDesktopViewport, showBanner, stage, popupIn]);

  const dockPopup = useCallback(() => {
    deferTask(() => setStage(1));
  }, []);

  useEffect(() => {
    if (variant !== 'popup') return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && stage === 0) dockPopup();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [variant, stage, dockPopup]);

  // Skeleton while the drive data is loading (banner variant only), so the
  // mobile jobfeed layout doesn't jump when the real banner resolves.
  if (variant === 'banner' && loading) {
    return (
      <Box
        aria-hidden="true"
        sx={{
          overflow: 'hidden',
          background:
            'linear-gradient(135deg,#FFF4E8 0%,#FDEAD8 55%,#FBE0C7 100%)',
          p: '8px 0 10px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.75,
        }}
      >
        <Skeleton
          variant="text"
          width={150}
          height={18}
          sx={{bgcolor: 'rgba(232,94,0,0.12)'}}
        />
        <Skeleton
          variant="text"
          width={200}
          height={14}
          sx={{bgcolor: 'rgba(232,94,0,0.1)'}}
        />
        <Skeleton
          variant="rounded"
          width={120}
          height={28}
          sx={{borderRadius: 999, bgcolor: 'rgba(232,94,0,0.18)'}}
        />
      </Box>
    );
  }

  if (!showBanner) return null;

  // Redirect to the live location's Talent Bridge detail page (ref: bridge_online)
  const inviteHref = `/${talentBridgeContants.redirectionUrl_temp}/${city}?ref=${talentBridgeContants.refCode_temp}`;

  // Inline full-width banner (used on mobile jobfeed, below the Download App banner)
  if (variant === 'banner') {
    const sparkles = [
      {top: '18%', left: '8%', delay: '0s', duration: '5.5s'},
      {top: '60%', left: '22%', delay: '.8s', duration: '6.5s'},
      {top: '26%', right: '12%', delay: '1.4s', duration: '5s'},
      {top: '66%', right: '26%', delay: '2.1s', duration: '7s'},
    ];

    return (
      <Box
        role="region"
        aria-label="Talent Bridge promotion"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 0,
          isolation: 'isolate',
          p: '8px 0 10px',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          '@keyframes tbWash': {
            '0%, 100%': {backgroundPosition: '0% 50%'},
            '50%': {backgroundPosition: '100% 50%'},
          },
          '@keyframes tbGlow': {
            '0%, 100%': {
              opacity: 0.55,
              transform: 'translateX(-50%) scale(1)',
            },
            '50%': {opacity: 1, transform: 'translateX(-50%) scale(1.12)'},
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            zIndex: -3,
            background:
              'linear-gradient(100deg,#FFF4E8 0%,#FDEAD8 34%,#FBE0C7 58%,#FFF4E8 100%)',
            backgroundSize: '220% 100%',
            animation: 'tbWash 14s ease-in-out infinite',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            zIndex: -2,
            top: '-60%',
            left: '50%',
            width: '60%',
            height: '220%',
            transform: 'translateX(-50%)',
            background:
              'radial-gradient(closest-side, rgba(255,171,94,.35), rgba(255,171,94,0) 70%)',
            animation: 'tbGlow 9s ease-in-out infinite',
          },
          '@media (prefers-reduced-motion: reduce)': {
            '&::before': {animation: 'none', backgroundPosition: '50% 50%'},
            '&::after': {animation: 'none', opacity: 0.7},
          },
        }}
      >
        {/* diagonal shine sweep */}
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: -1,
            pointerEvents: 'none',
            overflow: 'hidden',
            '@keyframes tbSweep': {
              '0%': {left: '-30%'},
              '55%, 100%': {left: '130%'},
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '-40%',
              bottom: '-40%',
              left: '-30%',
              width: '22%',
              transform: 'skewX(-18deg)',
              background:
                'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,.65) 50%, rgba(255,255,255,0) 100%)',
              animation: 'tbSweep 7s ease-in-out infinite',
            },
            '@media (prefers-reduced-motion: reduce)': {
              '&::before': {animation: 'none'},
            },
          }}
        />

        {/* drifting sparkles */}
        {sparkles.map((spark, i) => (
          <Box
            key={i}
            aria-hidden="true"
            sx={{
              position: 'absolute',
              zIndex: -1,
              fontSize: 11,
              lineHeight: 1,
              color: '#EFA85A',
              opacity: 0,
              pointerEvents: 'none',
              top: spark.top,
              left: spark.left,
              right: spark.right,
              '@keyframes tbTwinkle': {
                '0%, 100%': {opacity: 0, transform: 'scale(.6)'},
                '45%': {opacity: 0.9, transform: 'scale(1)'},
              },
              animation: `tbTwinkle ${spark.duration} ease-in-out ${spark.delay} infinite`,
              '@media (prefers-reduced-motion: reduce)': {animation: 'none'},
            }}
          >
            {i % 2 === 0 ? '✦' : '✧'}
          </Box>
        ))}

        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: 0.5,
          }}
        >
          <Typography
            sx={{fontSize: 15, fontWeight: 800, color: INK, lineHeight: 1.15}}
          >
            Hirist Talent Bridge
          </Typography>

          <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
            <Box
              aria-hidden="true"
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: ORANGE,
                flex: '0 0 auto',
                animation: 'tbPulse 2.2s ease-out infinite',
                '@keyframes tbPulse': {
                  '0%': {boxShadow: '0 0 0 0 rgba(232,94,0,.5)'},
                  '70%': {boxShadow: '0 0 0 7px rgba(232,94,0,0)'},
                  '100%': {boxShadow: '0 0 0 0 rgba(232,94,0,0)'},
                },
                '@media (prefers-reduced-motion: reduce)': {
                  animation: 'none',
                },
              }}
            />
            <Typography sx={{fontSize: 12, fontWeight: 500, color: INK}}>
              Live Hiring Drive
              {displayDate ? (
                <>
                  {' on '}
                  <Box component="span" sx={{fontWeight: 800}}>
                    {displayDate}
                  </Box>
                </>
              ) : (
                ''
              )}
            </Typography>
          </Box>

          <Box
            component="a"
            href={inviteHref}
            onClick={() =>
              deferTask(() =>
                TrackEventUBA('adBannerClick', 'click', {
                  source: `HTB ${getUbaPageName()} banner`,
                })
              )
            }
            sx={{
              mt: 0.25,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: 2.5,
              py: 0.6,
              borderRadius: 999,
              background: 'linear-gradient(135deg,#F5852A,#E85E00 55%,#C64E00)',
              backgroundSize: '180% 180%',
              color: '#fff',
              fontSize: 11.5,
              fontWeight: 800,
              letterSpacing: '.5px',
              textTransform: 'uppercase',
              textDecoration: 'none',
              boxShadow: '0 6px 14px -6px rgba(232,94,0,.5)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'transform .18s ease, box-shadow .18s ease',
              animation: 'tbBtnShift 6s ease-in-out infinite',
              '@keyframes tbBtnShift': {
                '0%, 100%': {backgroundPosition: '0% 50%'},
                '50%': {backgroundPosition: '100% 50%'},
              },
              '@keyframes tbBtnShine': {
                '0%': {left: '-120%'},
                '60%, 100%': {left: '130%'},
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-120%',
                width: '70%',
                height: '100%',
                background:
                  'linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,.45), rgba(255,255,255,0))',
                transform: 'skewX(-20deg)',
                animation: 'tbBtnShine 5.5s ease-in-out 1s infinite',
              },
              '& svg': {transition: 'transform .18s ease'},
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 8px 18px -6px rgba(232,94,0,.6)',
              },
              '&:hover svg': {transform: 'translateX(2px)'},
              '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
                '&::after': {animation: 'none'},
              },
            }}
          >
            {driveCta || 'Register Now'}
            <Box component="span" aria-hidden="true" sx={{display: 'flex'}}>
              <svg
                viewBox="0 0 24 24"
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.4}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 12h14M13 6l6 6-6 6" />
              </svg>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  if (variant === 'popup') {
    if (!isDesktopViewport) return null;

    const popupSubheading = (driveText || '').trim().toUpperCase();
    const popupSubLastSpace = popupSubheading.lastIndexOf(' ');
    const popupSubLead =
      popupSubLastSpace === -1
        ? ''
        : popupSubheading.slice(0, popupSubLastSpace);
    const popupSubTail =
      popupSubLastSpace === -1
        ? popupSubheading
        : popupSubheading.slice(popupSubLastSpace + 1);

    const popupVisible = stage === 0;
    const barVisible = stage === 1;

    // const popupSparkles = [
    //   {top: '10%', left: '6%'},
    //   {top: '20%', right: '46%'},
    //   {top: '68%', left: '10%'},
    //   {top: '78%', right: '44%'},
    //   {top: '14%', right: '10%'},
    // ];

    return (
      <>
        {/* backdrop (stage 0) */}
        <Box
          aria-hidden="true"
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            bgcolor: 'rgba(15,18,26,.55)',
            opacity: popupVisible && popupIn ? 1 : 0,
            transition: 'opacity .35s ease',
            pointerEvents: 'none',
            display: popupVisible ? 'block' : 'none',
          }}
        />

        {/* confetti burst canvas (stage 0, fires once per browser session) */}
        <Box
          component="canvas"
          ref={confettiCanvasRef}
          aria-hidden="true"
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1302,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            display: popupVisible ? 'block' : 'none',
          }}
        />

        {/* centered modal card (stage 0) */}
        <Box
          role="dialog"
          aria-modal="true"
          aria-label="Hirist Talent Bridge promotion"
          data-testid="talent-bridge-popup"
          data-component="TalentBridgePromoSlider"
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1301,
            display: popupVisible ? 'flex' : 'none',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
            pointerEvents: popupVisible ? 'auto' : 'none',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: 880,
              transform: popupIn
                ? 'translateY(0) scale(1)'
                : 'translateY(28px) scale(.92)',
              opacity: popupIn ? 1 : 0,
              transition:
                'transform .5s cubic-bezier(.34,1.56,.64,1), opacity .35s ease',
              '@media (prefers-reduced-motion: reduce)': {
                transition: 'opacity .2s ease',
                transform: 'none',
              },
            }}
          >
            <Box
              component="button"
              type="button"
              onClick={dockPopup}
              aria-label="Close and dock the Talent Bridge banner"
              title="Close"
              data-testid="talent-bridge-popup-dock"
              sx={{
                position: 'absolute',
                top: {xxs: -6, sm: -8},
                right: {xxs: -6, sm: -8},
                zIndex: 3,
                width: 34,
                height: 34,
                border: 'none',
                borderRadius: '50%',
                background: 'rgba(255,255,255,.95)',
                color: INK,
                boxShadow: '0 3px 10px rgba(20,25,40,.22)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'transform .18s ease, background .18s ease',
                padding: 0,
                '&:hover': {background: '#fff', transform: 'scale(1.08)'},
                '&:active': {transform: 'scale(.94)'},
              }}
            >
              <svg
                viewBox="0 0 24 24"
                width="15"
                height="15"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.4}
              >
                <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
              </svg>
            </Box>

            <GlobalStyles
              styles={{
                '@property --tb-angle': {
                  syntax: '"<angle>"',
                  inherits: 'false',
                  initialValue: '0deg',
                },
                '@keyframes tbBorderSweep': {
                  to: {'--tb-angle': '360deg'},
                },
              }}
            />
            <Box
              sx={{
                position: 'relative',
                borderRadius: {xxs: '18px', sm: '30px'},
                p: '3px',
                overflow: 'hidden',
                boxShadow: '0 28px 60px -20px rgba(232,94,0,.45)',
                background:
                  'conic-gradient(from var(--tb-angle, 0deg), #f0491e 0deg, #f0491e 210deg, #ff8a3d 265deg, #ffe3a8 300deg, #fff 318deg, #ffe3a8 336deg, #ff8a3d 360deg)',
                willChange: '--tb-angle',
                animation: 'tbBorderSweep 3.6s linear infinite',
                '@media (prefers-reduced-motion: reduce)': {
                  animation: 'none',
                  background: '#f0491e',
                },
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: {xxs: '16px', sm: '28px'},
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    backgroundImage: `url("${heroBgUrl}")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    backgroundSize: 'cover',
                    backgroundColor: '#fff2e4',
                    p: {xxs: '28px 20px', sm: '32px 40px 32px 32px'},
                    minHeight: {xxs: 260, sm: 260},
                    display: 'flex',
                    flexDirection: {xxs: 'column', sm: 'row'},
                    justifyContent: 'space-between',
                    alignItems: {xxs: 'center', sm: 'flex-start'},
                    gap: {xxs: 3, sm: 2},
                  }}
                >
                  {/* scattered sparkle confetti, same technique as the 'banner' variant */}
                  {/* {popupSparkles.map((spark, i) => (
                    <Box
                      key={i}
                      aria-hidden="true"
                      sx={{
                        position: 'absolute',
                        zIndex: 1,
                        fontSize: 14,
                        lineHeight: 1,
                        color: i % 3 === 0 ? '#22a565' : ORANGE,
                        pointerEvents: 'none',
                        top: spark.top,
                        left: spark.left,
                        right: spark.right,
                      }}
                    >
                      {i % 2 === 0 ? '✦' : '✧'}
                    </Box>
                  ))} */}

                  <Box
                    sx={{
                      position: 'absolute',
                      top: {xxs: 14, sm: 32},
                      right: {xxs: 56, sm: 20},
                      width: {xxs: 96, sm: 200},
                      zIndex: 1,
                    }}
                  >
                    <Box
                      component="img"
                      src={logoUrl}
                      alt="Hirist Talent Bridge"
                      sx={{width: '100%', height: 'auto', display: 'block'}}
                    />
                  </Box>

                  <Box
                    sx={{
                      position: 'relative',
                      zIndex: 1,
                      maxWidth: {xxs: '100%', sm: '58%'},
                      textAlign: {xxs: 'center', sm: 'left'},
                    }}
                  >
                    <Typography
                      component="div"
                      sx={{
                        fontSize: {xxs: 24, sm: 35},
                        fontWeight: 900,
                        textTransform: 'uppercase',
                        lineHeight: 1.1,
                      }}
                    >
                      <Box component="span" sx={{color: ORANGE}}>
                        TALENT BRIDGE
                      </Box>
                      {` IS BACK`}
                    </Typography>

                    {popupSubheading && (
                      <Typography
                        sx={{
                          fontSize: {xxs: 13, sm: 20},
                          fontWeight: 800,
                          textTransform: 'uppercase',
                          letterSpacing: '.4px',
                          color: INK,
                          mt: 0.75,
                        }}
                      >
                        {popupSubLead}
                        {popupSubLead ? ' ' : ''}
                        <Box component="span" sx={{color: ORANGE}}>
                          {popupSubTail}
                        </Box>
                      </Typography>
                    )}

                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: {xxs: 'center', sm: 'flex-start'},
                        columnGap: 2.5,
                        rowGap: 0.5,
                        mt: 2.5,
                      }}
                    >
                      {driveDate && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                          }}
                        >
                          <Box
                            component="img"
                            src={calendarIconUrl}
                            alt=""
                            aria-hidden="true"
                            sx={{width: 18, height: 18}}
                          />
                          <Typography
                            sx={{fontSize: 16, fontWeight: 600, color: INK}}
                          >
                            {driveDate}
                          </Typography>
                        </Box>
                      )}
                      {driveVenue && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                          }}
                        >
                          <Box
                            component="img"
                            src={mapIconUrl}
                            alt=""
                            aria-hidden="true"
                            sx={{width: 18, height: 18}}
                          />
                          <Typography
                            sx={{fontSize: 16, fontWeight: 600, color: INK}}
                          >
                            {driveVenue}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    <Box
                      component="a"
                      href={inviteHref}
                      aria-label={`Hirist Talent Bridge is back${
                        driveDate ? `, ${driveDate}` : ''
                      }${driveVenue ? ` at ${driveVenue}` : ''}. Apply now.`}
                      onClick={() =>
                        deferTask(() =>
                          TrackEventUBA('adBannerClick', 'click', {
                            source: `HTB ${getUbaPageName()} popup-mid`,
                          })
                        )
                      }
                      sx={{
                        mt: 7.5,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.75,
                        px: 4.5,
                        py: 1,
                        borderRadius: 999,
                        background:
                          'linear-gradient(90deg, #e9630c 0%, #c84d0a 100%)',
                        color: '#fff',
                        fontSize: 13,
                        fontWeight: 800,
                        letterSpacing: '.6px',
                        textTransform: 'uppercase',
                        textDecoration: 'none',
                        boxShadow: '0 8px 18px -6px rgba(232,94,0,.55)',
                        transition: 'transform .18s ease, box-shadow .18s ease',
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          boxShadow: '0 10px 22px -6px rgba(232,94,0,.65)',
                        },
                      }}
                    >
                      Apply Now
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2.5}
                        aria-hidden="true"
                      >
                        <path
                          d="M5 12h14M13 6l6 6-6 6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      position: {xxs: 'relative', sm: 'absolute'},
                      width: {xxs: 180, sm: 350},
                      right: {sm: 25},
                      bottom: {sm: -6},
                      display: 'flex',
                      justifyContent: 'center',
                      zIndex: 1,
                    }}
                  >
                    <Box
                      component="img"
                      src={illustrationUrl}
                      alt=""
                      aria-hidden="true"
                      sx={{width: '100%', height: 'auto', display: 'block'}}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* docked bottom bar (stage 1) */}
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1200,
            transform: barVisible ? 'translateY(0)' : 'translateY(110%)',
            transition: 'transform .62s cubic-bezier(.22,1,.36,1)',
            pointerEvents: barVisible ? 'auto' : 'none',
            '@media (prefers-reduced-motion: reduce)': {
              transition: 'opacity .2s ease',
              transform: 'none',
              opacity: barVisible ? 1 : 0,
            },
          }}
        >
          <Box
            role="region"
            aria-label="Talent Bridge promotion"
            data-testid="talent-bridge-popup-bar"
            data-component="TalentBridgePromoSlider"
            sx={{
              position: 'relative',
              height: 52,
              width: '100%',
              overflow: 'hidden',
              borderTop: '1px solid rgba(255,255,255,.6)',
              boxShadow: '0 -8px 24px -12px rgba(16,24,40,.35)',
              background:
                'linear-gradient(90deg,#fef8ec 0%,#fcdcb6 50%,#fef8ec 100%)',
            }}
          >
            <Box
              sx={{
                mx: 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: {xxs: 1.5, sm: 2},
                height: '100%',
                maxWidth: 1280,
                px: {xxs: 5, sm: 6},
              }}
            >
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 800,
                  color: '#2b2b2b',
                  whiteSpace: 'nowrap',
                }}
              >
                Hirist Talent Bridge
              </Typography>
              <Box
                sx={{
                  display: {xxs: 'none', sm: 'block'},
                  width: '1px',
                  height: 16,
                  bgcolor: '#e0b892',
                }}
              />
              <Box
                sx={{
                  display: {xxs: 'none', sm: 'flex'},
                  alignItems: 'center',
                  gap: 1,
                  whiteSpace: 'nowrap',
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: '#F0491E',
                  }}
                />
                <Typography sx={{fontSize: 13, color: '#4a4a4a'}}>
                  Live Hiring Drive on{' '}
                  <Box
                    component="span"
                    sx={{fontWeight: 700, color: '#2b2b2b'}}
                  >
                    {displayDate || driveDate}
                  </Box>
                </Typography>
              </Box>
              <Box
                component="a"
                href={inviteHref}
                data-testid="talent-bridge-popup-bar-cta"
                onClick={() =>
                  deferTask(() =>
                    TrackEventUBA('adBannerClick', 'click', {
                      source: `HTB ${getUbaPageName()} popup-bottom`,
                    })
                  )
                }
                sx={{
                  ml: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  whiteSpace: 'nowrap',
                  borderRadius: 999,
                  px: 2,
                  py: 0.75,
                  fontSize: 12,
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: '.4px',
                  color: '#fff',
                  textDecoration: 'none',
                  background: 'linear-gradient(90deg,#f0491e,#ff8a3d,#ffb347)',
                  boxShadow: '0 2px 8px -1px rgba(240,73,30,.5)',
                  transition: 'transform .18s ease',
                  '&:hover': {transform: 'scale(1.03)'},
                }}
              >
                Apply Now
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.5}
                  aria-hidden="true"
                >
                  <path
                    d="M5 12h14M13 6l6 6-6 6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Box>
            </Box>
          </Box>
        </Box>
      </>
    );
  }

  const promoClassName = `${promoIn ? 'in' : ''} ${
    promoExit ? 'exit-down' : ''
  }`.trim();

  // Split the dynamic tagline so the last word renders in the accent colour,
  // e.g. "New Age Tech & Data Roles" -> "NEW AGE TECH & DATA" + "ROLES"
  const subheading = (driveText || '').trim().toUpperCase();
  const subLastSpace = subheading.lastIndexOf(' ');
  const subLead = subLastSpace === -1 ? '' : subheading.slice(0, subLastSpace);
  const subTail =
    subLastSpace === -1 ? subheading : subheading.slice(subLastSpace + 1);

  return (
    <>
      {/* full docked banner (slides in from the left) */}
      <Box
        className={promoClassName}
        role="dialog"
        aria-label="Talent Bridge promotion"
        sx={theme => ({
          position: 'fixed',
          left: 24,
          bottom: 24,
          width: '360px',
          zIndex: 1200,
          transform: 'translateX(calc(-100% - 48px))',
          opacity: 0,
          transition:
            'transform .55s cubic-bezier(.22,.9,.3,1), opacity .35s ease',
          // keep text crisp: don't leave the card on a promoted GPU layer,
          // which softens font rendering after the slide-in finishes
          backfaceVisibility: 'hidden',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          '&.in': {transform: 'translateX(0)', opacity: 1},
          '&.exit-down': {
            transform: 'translateY(140%)',
            opacity: 0,
            transition: 'transform .4s ease-in, opacity .3s ease',
          },
          [theme.breakpoints.down('sm')]: {
            left: 16,
            bottom: 16,
            width: 'calc(100vw - 32px)',
          },
          '@media (prefers-reduced-motion: reduce)': {
            transition: 'opacity .2s ease',
            transform: 'none',
            '&.in': {transform: 'none'},
          },
        })}
      >
        <Box
          sx={{
            position: 'relative',
            borderRadius: '18px',
            overflow: 'hidden',
            boxShadow: SHADOW,
            background: '#fff',
            border: '3px solid #fff',
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={closePromo}
            aria-label="Close promotion"
            title="Close"
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              width: 30,
              height: 30,
              border: 'none',
              borderRadius: '50%',
              background: 'rgba(255,255,255,.92)',
              color: INK,
              boxShadow: '0 3px 10px rgba(20,25,40,.22)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'transform .18s ease, background .18s ease',
              zIndex: 2,
              padding: 0,
              '&:hover': {background: '#fff', transform: 'scale(1.08)'},
              '&:active': {transform: 'scale(.94)'},
              '& svg': {
                width: 14,
                height: 14,
                stroke: 'currentColor',
                strokeWidth: 2.4,
                fill: 'none',
              },
            }}
          >
            <svg viewBox="0 0 24 24">
              <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
            </svg>
          </Box>

          <Box
            component="a"
            href={inviteHref}
            aria-label={`Hirist Talent Bridge is back${
              driveDate ? `, ${driveDate}` : ''
            }${driveVenue ? ` at ${driveVenue}` : ''}. Apply now.`}
            onClick={() =>
              deferTask(() =>
                TrackEventUBA('adBannerClick', 'click', {
                  source: `HTB ${getUbaPageName()} slider`,
                })
              )
            }
            sx={{
              position: 'relative',
              display: 'block',
              textDecoration: 'none',
              overflow: 'hidden',
              p: {xxs: '12px 14px 14px', sm: '14px 16px 16px'},
              background:
                'linear-gradient(135deg,#FFF4E8 0%,#FDEAD8 55%,#FBE0C7 100%)',
              // soft diagonal sheen, like the reference creative
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background:
                  'linear-gradient(115deg, transparent 40%, rgba(255,255,255,.22) 50%, transparent 60%)',
                pointerEvents: 'none',
              },
            }}
          >
            <Box sx={{position: 'relative'}}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 0.5,
                }}
              >
                <Box
                  component="img"
                  src="https://hirist-assets.s3.ap-south-1.amazonaws.com/hirist-talent-bridge.webp"
                  alt="Hirist Talent Bridge"
                  sx={{height: 20, width: 'auto', display: 'block'}}
                />
              </Box>

              <Typography
                sx={{
                  textAlign: 'center',
                  fontSize: {xxs: 18, sm: 19},
                  fontWeight: 900,
                  lineHeight: 1.05,
                  textTransform: 'uppercase',
                  letterSpacing: '.2px',
                }}
              >
                <Box component="span" sx={{color: ORANGE}}>
                  Talent Bridge{' '}
                </Box>
                <Box component="span" sx={{color: INK}}>
                  is Back
                </Box>
              </Typography>

              {subheading && (
                <Typography
                  sx={{
                    textAlign: 'center',
                    fontSize: {xxs: 10.5, sm: 11},
                    fontWeight: 800,
                    letterSpacing: '.4px',
                    textTransform: 'uppercase',
                    mt: 0.25,
                    color: INK,
                  }}
                >
                  {subLead}
                  {subLead ? ' ' : ''}
                  <Box component="span" sx={{color: ORANGE}}>
                    {subTail}
                  </Box>
                </Typography>
              )}

              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'center',
                  columnGap: {xxs: 1.25, sm: 1.75},
                  rowGap: 0.5,
                  mt: 1,
                }}
              >
                {driveDate && (
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                    <Box
                      aria-hidden="true"
                      sx={{display: 'flex', color: ORANGE}}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width="15"
                        height="15"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.8}
                      >
                        <rect x="3" y="4.5" width="18" height="16" rx="3" />
                        <path d="M3 9h18" />
                        <path d="M8 3v3M16 3v3" strokeLinecap="round" />
                      </svg>
                    </Box>
                    <Typography
                      sx={{fontSize: 11.5, fontWeight: 600, color: INK}}
                    >
                      {driveDate}
                    </Typography>
                  </Box>
                )}
                {driveVenue && (
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                    <Box
                      aria-hidden="true"
                      sx={{display: 'flex', color: ORANGE}}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width="15"
                        height="15"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.8}
                      >
                        <path d="M12 21s7-6.4 7-11a7 7 0 1 0-14 0c0 4.6 7 11 7 11Z" />
                        <circle cx="12" cy="10" r="2.5" />
                      </svg>
                    </Box>
                    <Typography
                      sx={{fontSize: 11.5, fontWeight: 600, color: INK}}
                    >
                      {driveVenue}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box sx={{display: 'flex', justifyContent: 'center', mt: 1.25}}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 2.75,
                    py: 0.75,
                    borderRadius: 999,
                    background:
                      'linear-gradient(180deg,#F58220 0%,#E85E00 100%)',
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 800,
                    letterSpacing: '.6px',
                    textTransform: 'uppercase',
                    boxShadow: '0 8px 18px -6px rgba(232,94,0,.55)',
                    transition: 'transform .18s ease, box-shadow .18s ease',
                  }}
                >
                  Apply Now
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* minimized pill, bottom-middle */}
      <Box
        component="button"
        type="button"
        className={miniIn ? 'in' : ''}
        onClick={openPromo}
        aria-label="Reopen Talent Bridge promotion"
        sx={{
          position: 'fixed',
          left: '50%',
          bottom: 20,
          transform: 'translateX(-50%) translateY(160%)',
          zIndex: 1200,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '8px 16px 8px 8px',
          background: '#fff',
          borderRadius: 999,
          boxShadow: SHADOW,
          cursor: 'pointer',
          border: '1px solid rgba(232,94,0,.18)',
          transition:
            'transform .5s cubic-bezier(.22,.9,.3,1), box-shadow .2s ease',
          maxWidth: 'calc(100vw - 32px)',
          '&.in': {transform: 'translateX(-50%) translateY(0)'},
          '&:hover': {
            boxShadow:
              '0 22px 46px -14px rgba(232,94,0,.4),0 4px 12px rgba(20,25,40,.14)',
          },
          '@media (prefers-reduced-motion: reduce)': {
            transition: 'opacity .2s ease',
            transform: 'translateX(-50%)',
            '&.in': {transform: 'translateX(-50%)'},
          },
        }}
      >
        <Box
          component="span"
          aria-hidden="true"
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: 'linear-gradient(135deg,#FFE6D2,#FFD3B0)',
            flex: '0 0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: 'inset 0 0 0 1px rgba(232,94,0,.16)',
            '& svg': {
              width: 21,
              height: 21,
              stroke: ORANGE,
              strokeWidth: 1.9,
              fill: 'none',
            },
          }}
        >
          <svg viewBox="0 0 24 24">
            <rect x="3" y="8" width="18" height="12" rx="2.2" />
            <path d="M8 8V6.4A2.4 2.4 0 0 1 10.4 4h3.2A2.4 2.4 0 0 1 16 6.4V8" />
            <path d="M3 13h18" strokeLinecap="round" />
          </svg>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            lineHeight: 1.15,
            textAlign: 'left',
          }}
        >
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: INK,
              whiteSpace: 'nowrap',
            }}
          >
            Talent Bridge is back
          </Typography>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 500,
              color: ORANGE,
              whiteSpace: 'nowrap',
            }}
          >
            Tech &amp; data roles · Apply
          </Typography>
        </Box>
        <Box
          component="span"
          aria-hidden="true"
          sx={{
            width: 26,
            height: 26,
            borderRadius: '50%',
            background: ORANGE,
            flex: '0 0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: '2px',
            '& svg': {
              width: 13,
              height: 13,
              stroke: '#fff',
              strokeWidth: 2.6,
              fill: 'none',
            },
          }}
        >
          <svg viewBox="0 0 24 24">
            <path
              d="M6 15l6-6 6 6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Box>
      </Box>
    </>
  );
};

export default TalentBridgePromoSlider;
